import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { inr } from "@/lib/api";
import { fmt12 } from "@/lib/time";
import { Calendar, Plus, Clock, CheckCircle2, AlertCircle, Truck, PhoneCall, MessageCircle } from "lucide-react";

export default function CustomerAppointments() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bookings/mine")
      .then((r) => setBookings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeDoorstep = bookings.find(
    (b) => b.booking_type === "doorstep" && b.status !== "completed" && b.status !== "cancelled"
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-amber-400 font-bold uppercase tracking-widest animate-pulse">Loading Appointments...</div>
      </div>
    );
  }

  return (
    <div data-testid="customer-appointments" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-white/10 pb-6"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
            <Calendar size={16} /> RESERVATIONS & VISITS
          </div>
          <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            My Appointments
          </h1>
        </div>

        <Link
          to="/book"
          className="gold-btn rounded-xl py-3 px-6 font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40 flex items-center gap-2"
        >
          <Plus size={16} /> Book New Session
        </Link>
      </motion.div>

      {/* Active Doorstep Request Banner */}
      {activeDoorstep && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-6 rounded-2xl border-2 border-emerald-400/80 bg-gradient-to-r from-emerald-950/60 via-black to-teal-950/60 shadow-2xl space-y-4"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-white/10 pb-3">
            <div className="flex items-center gap-2 text-emerald-400 font-extrabold text-xs uppercase tracking-widest">
              <Truck size={18} /> ACTIVE DOORSTEP REQUEST ({activeDoorstep.id?.slice(0, 8)})
            </div>
            <span className="px-3 py-1 rounded-full text-[10px] font-extrabold uppercase bg-amber-400/20 text-amber-300 border border-amber-400/40">
              STATUS: {activeDoorstep.status?.toUpperCase() || "PENDING CONFIRMATION"}
            </span>
          </div>

          <div className="space-y-1">
            <h3 className="font-heading text-lg font-bold text-white">
              Our team will connect with you shortly!
            </h3>
            <p className="text-xs text-white/80 leading-relaxed font-medium">
              Your doorstep request for {activeDoorstep.date} ({fmt12(activeDoorstep.time)}) is active. You can also call our branch manager directly right now.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <a
              href="tel:+919560656913"
              className="gold-btn py-2.5 px-4 text-xs font-extrabold uppercase tracking-wider rounded-xl flex items-center gap-2 shadow-lg"
            >
              <PhoneCall size={14} /> Call Branch (+91 9560656913)
            </a>
            <a
              href={`https://wa.me/919560656913?text=Hi%20Hammam%20Spa,%20I%20have%20an%20active%20doorstep%20booking%20request%20(${activeDoorstep.id?.slice(0, 8)}).`}
              target="_blank"
              rel="noreferrer"
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold py-2.5 px-4 text-xs uppercase tracking-wider rounded-xl flex items-center gap-2 shadow-lg transition-colors"
            >
              <MessageCircle size={14} /> WhatsApp Us
            </a>
          </div>
        </motion.div>
      )}

      {/* Appointments Table Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="rounded-2xl border border-amber-400/70 overflow-hidden bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-2xl shadow-yellow-950/20"
      >
        <div className="p-5 border-b border-white/10 text-xs font-extrabold uppercase tracking-widest text-amber-400 flex items-center justify-between">
          <span>APPOINTMENT HISTORY</span>
          <span className="text-white/40">{bookings.length} Total Visits</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 bg-black/60">
              <tr>
                {["Date", "Time", "Type", "Status", "Payment", "Amount"].map((h) => (
                  <th key={h} className="text-left px-6 py-4 text-xs font-extrabold uppercase tracking-widest text-white/50">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 font-medium">
              {bookings.map((b) => {
                const isCompleted = b.status === "completed";
                const isPending = b.status === "pending";
                const isPaid = b.payment_status === "paid";

                return (
                  <tr key={b.id} className="hover:bg-white/[0.02] transition-all">
                    <td className="px-6 py-4 text-white font-bold">{b.date}</td>
                    <td className="px-6 py-4 text-amber-300 font-semibold">{fmt12(b.time)}</td>
                    <td className="px-6 py-4">
                      <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-md border border-white/15 bg-white/5 text-white/80">
                        {b.booking_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full inline-flex items-center gap-1.5 ${
                          isCompleted
                            ? "bg-emerald-950/80 border border-emerald-400 text-emerald-300"
                            : isPending
                            ? "bg-amber-950/80 border border-amber-400 text-amber-300"
                            : "bg-red-950/80 border border-red-500 text-red-300"
                        }`}
                      >
                        {isCompleted && <CheckCircle2 size={11} />}
                        {isPending && <Clock size={11} />}
                        {!isCompleted && !isPending && <AlertCircle size={11} />}
                        {b.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-md ${
                          b.payment_mode === "wallet" || isPaid
                            ? "text-emerald-300 bg-emerald-950/80 border border-emerald-400"
                            : "text-amber-300 bg-amber-950/80 border border-amber-400"
                        }`}
                      >
                        {b.payment_mode === "wallet"
                          ? "💳 WALLET PAID"
                          : isPaid
                          ? "✅ PAID"
                          : (b.payment_status || "PAY AT COUNTER").replace("_", " ").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-heading text-base font-bold text-amber-300">
                      {inr(b.amount)}
                    </td>
                  </tr>
                );
              })}

              {bookings.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-16 text-white/50 font-medium">
                    No appointments reserved yet.{" "}
                    <Link to="/book" className="text-amber-400 hover:underline font-bold">
                      Book your first session →
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
